import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { HORSE_IMAGE } from "@/lib/content";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== false) navigate("/portal", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to the firm.");
      navigate("/portal", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 bg-ivory grid grid-cols-1 lg:grid-cols-12" data-testid="register-page">
      <aside className="hidden lg:block lg:col-span-5 panel-navy relative overflow-hidden order-2 lg:col-start-8">
        <img src={HORSE_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-navy/70"></div>
        <div className="relative h-full flex flex-col justify-end p-16 text-ivory">
          <div className="kicker kicker-ivory">Open an Account</div>
          <h2 className="mt-6 font-serif text-5xl leading-[1.0]">
            Begin a relationship<br/>
            <span className="italic text-brass">that lasts.</span>
          </h2>
          <p className="mt-6 text-ivory/70 font-light leading-relaxed max-w-sm">
            Opening a client account allows you to submit intake requests, review the status of your
            matters, and correspond with the firm in privilege.
          </p>
        </div>
      </aside>

      <div className="lg:col-span-6 flex items-center px-6 md:px-12 py-20">
        <form onSubmit={submit} className="w-full max-w-md mx-auto" data-testid="register-form">
          <div className="kicker">New Client</div>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.98]">
            Open an<br/>
            <span className="italic text-oxblood">account.</span>
          </h1>

          <div className="mt-12 space-y-8">
            <label className="block">
              <div className="field-label mb-2">Full Name</div>
              <input className="field" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" />
            </label>
            <label className="block">
              <div className="field-label mb-2">Email</div>
              <input className="field" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
            </label>
            <label className="block">
              <div className="field-label mb-2">Password (min. 6 characters)</div>
              <input className="field" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
            </label>
          </div>

          {error && (
            <div className="mt-6 text-oxblood font-serif italic text-sm" data-testid="register-error">— {error}</div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-40" data-testid="register-submit">
              {loading ? "Creating…" : "Open Account"}
            </button>
            <Link to="/login" className="link-underline font-serif italic" data-testid="register-login-link">
              Already a client?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
